import { useEffect, useState } from "react";
import { Globe, MessageCircle, Instagram, Mail, MapPin, ChefHat, LayoutDashboard, TrendingUp, Smartphone, ArrowRight, Sparkles, Shield, Clock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_08f655cb-8b91-4c06-80bd-b94d5f4523ca/artifacts/kxmzqj5h_ChatGPT%20Image%2027%20de%20fev.%20de%202026%2C%2001_06_03.png";

const LINKS = [
  {
    label: "Comece Gratis",
    sub: "29 dias de teste sem compromisso",
    href: "https://kaisosystem.com/landing",
    icon: Sparkles,
    style: "bg-[#D4AF37] text-black hover:bg-[#FCC200] font-bold shadow-[0_0_25px_rgba(212,175,55,0.35)]",
  },
  {
    label: "WhatsApp",
    sub: "Fale com o suporte agora",
    href: "https://wa.me/34611564839",
    icon: MessageCircle,
    style: "bg-[#25D366] text-white hover:bg-[#1ebd59] shadow-lg shadow-green-900/20",
  },
  {
    label: "Nosso Website",
    sub: "kaisosystem.com",
    href: "https://kaisosystem.com",
    icon: Globe,
    style: "bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-[#D4AF37]/40",
  },
  {
    label: "Instagram",
    sub: "@kaisosystem",
    href: "https://www.instagram.com/kaisosystem",
    icon: Instagram,
    style: "bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-[#D4AF37]/40",
  },
  {
    label: "Email",
    sub: "suporte@kaisosystem.com",
    href: "mailto:suporte@kaisosystem.com",
    icon: Mail,
    style: "bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-[#D4AF37]/40",
  },
];

const FEATURES = [
  { icon: LayoutDashboard, label: "Gestao Completa", desc: "Pedidos, mesas, estoque e equipe" },
  { icon: ChefHat, label: "Controle de Cozinha", desc: "Monitor KDS em tempo real" },
  { icon: TrendingUp, label: "Relatorios IA", desc: "Insights financeiros inteligentes" },
  { icon: Smartphone, label: "Menu Digital", desc: "Cardapio online com QR Code" },
  { icon: Shield, label: "Ponto de Venda", desc: "PDV completo e integrado" },
  { icon: Clock, label: "Suporte 24/7", desc: "Atendimento por IA e WhatsApp" },
];

function FeatureCard({ icon: Icon, label, desc, delay }) {
  return (
    <div
      className="bg-[#121212] border border-white/5 rounded-2xl p-4 flex flex-col items-center text-center gap-1.5 hover:border-[#D4AF37]/50 transition-all duration-300 group relative overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`feature-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(circle at center, rgba(212,175,55,0.08) 0%, transparent 70%)" }} />
      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
        <Icon className="w-5 h-5 text-[#D4AF37]" />
      </div>
      <span className="font-bold text-white text-sm tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{label}</span>
      <span className="text-xs text-zinc-500 leading-tight">{desc}</span>
    </div>
  );
}

function LinkButton({ label, sub, href, icon: Icon, style, delay }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`relative flex items-center gap-4 rounded-xl h-14 px-5 w-full font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${style}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`link-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <div className="flex flex-col items-start min-w-0">
        <span className="text-sm font-bold leading-tight tracking-wide">{label}</span>
        {sub && <span className="text-[11px] opacity-70 leading-tight truncate">{sub}</span>}
      </div>
      <ArrowRight className="w-4 h-4 ml-auto flex-shrink-0 opacity-50" />
    </a>
  );
}

export default function LinkPage() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden" data-testid="link-page">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px]" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[300px] h-[300px]" style={{ background: "radial-gradient(circle, rgba(220,38,38,0.04) 0%, transparent 60%)" }} />
      </div>

      <div className={`relative max-w-md mx-auto px-5 py-8 md:py-12 transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>

        {/* Logo + Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full blur-2xl" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.25) 0%, transparent 70%)" }} />
            <img
              src={LOGO_URL}
              alt="Kaiso System"
              className="relative w-24 h-24 rounded-2xl object-cover shadow-2xl shadow-black/60 ring-2 ring-[#D4AF37]/20"
              data-testid="link-logo"
            />
          </div>
          <h1 className="font-bold text-2xl text-white tracking-wider uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Kaiso System
          </h1>
          <p className="text-zinc-400 text-sm text-center mt-2 max-w-xs leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>
            O sistema de gestao para restaurantes que revoluciona o seu negocio
          </p>
        </div>

        {/* Free Trial Banner */}
        <div className="relative mb-6 rounded-2xl overflow-hidden" data-testid="trial-banner">
          <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/20 via-[#D4AF37]/5 to-[#DC2626]/10 blur-sm" />
          <div className="relative bg-[#121212]/90 backdrop-blur-xl border border-[#D4AF37]/20 rounded-2xl p-5 text-center">
            <div className="inline-flex items-center gap-1.5 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
              <Sparkles className="w-3 h-3" /> Oferta Especial
            </div>
            <h2 className="font-bold text-xl text-white tracking-wide uppercase mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              29 Dias Gratis
            </h2>
            <p className="text-zinc-400 text-xs mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              Teste todas as funcionalidades sem compromisso. Sem cartao de credito.
            </p>
            <a
              href="https://kaisosystem.com/landing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold text-sm uppercase tracking-wider h-12 px-8 rounded-xl shadow-[0_0_25px_rgba(212,175,55,0.35)] hover:bg-[#FCC200] transition-all hover:scale-[1.03] active:scale-[0.97]"
              data-testid="link-trial-cta"
            >
              Comecar Agora <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-6">
          <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-[0.2em] mb-3 px-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            O que voce controla
          </h3>
          <div className="grid grid-cols-3 gap-2.5">
            {FEATURES.map((f, i) => (
              <FeatureCard key={f.label} {...f} delay={i * 80} />
            ))}
          </div>
        </div>

        {/* Links */}
        <div className="space-y-2.5 mb-8">
          <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-[0.2em] mb-3 px-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Conecte-se
          </h3>
          {LINKS.map((link, i) => (
            <LinkButton key={link.label} {...link} delay={i * 100} />
          ))}
        </div>

        {/* Company Footer */}
        <footer className="text-center border-t border-white/5 pt-6 pb-4" data-testid="link-footer">
          <div className="flex items-center justify-center gap-1.5 text-zinc-600 text-xs mb-2">
            <MapPin className="w-3 h-3" />
            <span style={{ fontFamily: "Manrope, sans-serif" }}>Avenida Barcelona, 19 - Cordoba, Espana</span>
          </div>
          <p className="text-zinc-700 text-[10px] tracking-wider uppercase" style={{ fontFamily: "Manrope, sans-serif" }}>
            Kaiso System &copy; {new Date().getFullYear()} &mdash; Todos os direitos reservados
          </p>
        </footer>
      </div>
    </div>
  );
}
